import { toMoneyAmount } from '@/common/domain/money/toMoneyAmount';
import type { LiveDocumentEditingConfig } from '@/document-render/presentation/editing/quote/LiveDocumentEditingConfig';
import { LineItemQuoteRow, type QuoteLineItem } from '@/document-render/presentation/blocks/lineItems/LineItemQuoteRow';
import { reorderQuoteLineIds } from '@/document-render/presentation/blocks/lineItems/useLineItemReorder';
import { CanvasRevealGroup } from '@/document-render/presentation/editing/quote/CanvasRevealGroup';
import {
  LINE_ITEM_ADD_BUTTON_STYLE,
  LINE_ITEM_COLUMNS,
  LINE_ITEMS_TABLE_STYLE,
  lineItemColStyle,
  lineItemHeadCellStyle
} from '@/document-render/presentation/blocks/lineItems/lineItemsQuoteStyles';
import { VISUALLY_HIDDEN_STYLE } from '@/document-render/presentation/blocks/priceBook/priceBookSheetStyles';

function readString(row: Record<string, unknown>, key: string): string | undefined {
  const value = row[key];
  return typeof value === 'string' ? value : undefined;
}

function readNumber(row: Record<string, unknown>, key: string): number | null {
  const value = row[key];
  return toMoneyAmount(value);
}

export function toQuoteLineItems(rows: readonly unknown[]): QuoteLineItem[] {
  return rows.flatMap((row) => {
    if (typeof row !== 'object' || row === null) return [];
    const record = row as Record<string, unknown>;
    return [
      {
        id: readString(record, 'id'),
        description: readString(record, 'description') ?? readString(record, 'name'),
        quantity: readNumber(record, 'quantity'),
        rate: readNumber(record, 'rate'),
        unit: readString(record, 'unit'),
        origin: readString(record, 'origin'),
        locked: record.locked === true
      }
    ];
  });
}

export function LineItemsQuoteTable({
  rows,
  allRows = rows,
  editing,
  currency,
  cultureTag,
  showHeader = true,
  showAdd = true,
  headerFontSize
}: {
  /** The rows this page slice prints. */
  rows: QuoteLineItem[];
  /** Every row of the quote, in order — reorder ids and move limits are relative to the full list. */
  allRows?: QuoteLineItem[];
  editing: LiveDocumentEditingConfig;
  currency: string;
  cultureTag: string;
  showHeader?: boolean;
  showAdd?: boolean;
  headerFontSize?: number | string;
}) {
  const move = (index: number, direction: -1 | 1) => {
    const ids = allRows.flatMap((row) => (row.id ? [row.id] : []));
    const from = rows[index]?.id;
    if (!from) return;
    const fromIndex = ids.indexOf(from);
    editing.onReorderLineItems?.(reorderQuoteLineIds(ids, fromIndex, fromIndex + direction));
  };

  const canMove = (row: QuoteLineItem, direction: -1 | 1) => {
    const ids = allRows.flatMap((candidate) => (candidate.id ? [candidate.id] : []));
    const at = row.id ? ids.indexOf(row.id) : -1;
    return at >= 0 && at + direction >= 0 && at + direction < ids.length;
  };

  return (
    <table aria-label="Line items" style={LINE_ITEMS_TABLE_STYLE}>
      <colgroup>
        {LINE_ITEM_COLUMNS.map((column) => (
          <col key={column.key} style={lineItemColStyle(column)} />
        ))}
      </colgroup>
      {showHeader ? (
        <thead>
          <tr>
            {LINE_ITEM_COLUMNS.map((column) => (
              <th key={column.key} scope="col" style={lineItemHeadCellStyle(column.align, headerFontSize)}>
                {/* The Actions column has no visible title: its cells hold a "⋮" menu. */}
                {column.key === 'actions' ? <span style={VISUALLY_HIDDEN_STYLE}>{column.label}</span> : column.label}
              </th>
            ))}
          </tr>
        </thead>
      ) : null}
      <tbody>
        {rows.map((row, index) => (
          <LineItemQuoteRow
            key={row.id ?? `readonly-${index}`}
            row={row}
            currency={currency}
            cultureTag={cultureTag}
            canMoveUp={canMove(row, -1)}
            canMoveDown={canMove(row, 1)}
            onEditCell={editing.onEditLineItemCell}
            onDelete={editing.onDeleteLineItem}
            onMoveUp={() => move(index, -1)}
            onMoveDown={() => move(index, 1)}
          />
        ))}
      </tbody>
      {editing.onAddLineItem && showAdd ? (
        <tfoot>
          <tr>
            <td colSpan={6}>
              <CanvasRevealGroup>
                <button type="button" aria-label="Add line item" style={LINE_ITEM_ADD_BUTTON_STYLE} onClick={editing.onAddLineItem}>
                  Add line item
                </button>
              </CanvasRevealGroup>
            </td>
          </tr>
        </tfoot>
      ) : null}
    </table>
  );
}
